import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, DoorOpen, ShieldAlert } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";
import { Reveal, Eyebrow } from "../components/Reveal";
import ServiceIcon, { SERVICE_ORDER } from "../components/ServiceIcon";

function QuickExit({ label, ariaLabel }) {
  return (
    <a
      href="https://www.google.com/search?q=weather"
      data-testid="vawa-quick-exit-button"
      aria-label={ariaLabel}
      className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-3.5 text-sm font-bold text-white shadow-2xl transition-transform duration-150 hover:scale-105 active:scale-95"
    >
      <DoorOpen className="h-4 w-4" />
      {label}
    </a>
  );
}

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const { t } = useLang();
  const s = t.services[slug];

  if (!s || !SERVICE_ORDER.includes(slug)) {
    return (
      <main className="bg-cream pt-40 pb-24" data-testid="service-not-found">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h1 className="font-serif text-4xl text-espresso">{t.notFound.title}</h1>
          <Link to="/services" className="mt-6 inline-flex items-center gap-2 font-semibold text-teal">
            <ArrowLeft className="h-4 w-4" /> {t.common.backToServices}
          </Link>
        </div>
      </main>
    );
  }

  const isVawa = slug === "vawa";

  return (
    <main data-testid={`service-detail-${slug}`}>
      <Seo title={`${s.name} — Desert Coast Immigration`} desc={s.short} />
      {isVawa && <QuickExit label={s.quickExit} ariaLabel={s.quickExitLabel} />}

      <section className="relative overflow-hidden bg-cream grain pt-32 pb-14 md:pt-40 md:pb-16">
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-sand/40 blur-2xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Link
              to="/services"
              data-testid="service-back-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-teal hover:text-espresso transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {t.common.backToServices}
            </Link>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-teal/10 text-teal">
                <ServiceIcon slug={slug} className="h-8 w-8" />
              </span>
              <div>
                <Eyebrow>{t.servicesIndex.eyebrow}</Eyebrow>
                <h1 className="mt-1 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-espresso">
                  {s.name}
                </h1>
              </div>
            </div>
            <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-espresso/75">{s.short}</p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <span
                className="inline-flex items-baseline gap-2 rounded-full bg-espresso px-5 py-2.5 text-cream"
                data-testid="service-price-badge"
              >
                <span className="text-xs uppercase tracking-wider text-sand">{t.common.serviceFee}</span>
                <span className="font-mono text-lg font-medium">
                  {s.price || t.common.customQuote}
                  {s.priceNote ? <span className="text-sm text-sand"> {s.priceNote}</span> : null}
                </span>
              </span>
              <Link
                to={`/contact?service=${slug}`}
                data-testid="service-contact-button"
                className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-espresso active:scale-95"
              >
                {t.common.getStarted}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {isVawa && (
        <div className="bg-teal/10 border-y border-teal/20" data-testid="vawa-safety-banner">
          <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-4 sm:px-6 lg:px-8">
            <ShieldAlert className="h-5 w-5 shrink-0 text-teal" />
            <p className="text-sm font-medium text-espresso">{s.safetyNote}</p>
          </div>
        </div>
      )}

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-5">
            {s.overview.map((p, i) => (
              <Reveal key={i} delay={0.05 * i}>
                <p className="text-base leading-relaxed text-espresso/85">{p}</p>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 grid gap-8 lg:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-2xl border border-sand/50 bg-cream/60 p-8" data-testid="service-process-card">
                <h2 className="font-serif text-2xl font-medium text-espresso">{s.processTitle}</h2>
                <ol className="mt-6 space-y-5">
                  {s.process.map((step, i) => (
                    <li key={i} className="flex gap-4">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-terracotta font-mono text-sm font-semibold text-white">
                        {i + 1}
                      </span>
                      <p className="pt-1 text-sm leading-relaxed text-espresso/80">{step}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className={`h-full rounded-2xl border p-8 ${isVawa ? "border-teal/30 bg-teal/5" : "border-sand/50 bg-cream/60"}`} data-testid="service-documents-card">
                <h2 className="font-serif text-2xl font-medium text-espresso">{s.documentsTitle}</h2>
                <ul className="mt-6 space-y-4">
                  {s.documents.map((doc, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-espresso/80">
                      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${isVawa ? "bg-teal" : "bg-terracotta"}`} />
                      {doc}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <Reveal className="mt-16 max-w-3xl">
            <h2 className="font-serif text-2xl font-medium text-espresso" data-testid="service-faq-heading">
              {t.faq.title}
            </h2>
            <Accordion type="single" collapsible className="mt-4" data-testid="service-faq-accordion">
              {s.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-sand/50">
                  <AccordionTrigger data-testid={`service-faq-trigger-${i}`} className="text-left font-medium text-espresso hover:text-terracotta">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-espresso/75">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          <Reveal className="mt-16">
            <div className="rounded-2xl bg-espresso p-8 sm:p-10 grain relative overflow-hidden" data-testid="service-cta-band">
              <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-terracotta/25 blur-2xl" aria-hidden="true" />
              <div className="relative flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
                <div>
                  <p className="font-serif text-2xl font-medium text-cream">{t.home.ctaTitle}</p>
                  <p className="mt-2 max-w-xl text-sm leading-relaxed text-cream/70">{t.common.govFeesNote}</p>
                </div>
                <Link
                  to={`/contact?service=${slug}`}
                  data-testid="service-cta-button"
                  className="shrink-0 rounded-full bg-terracotta px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-sand hover:text-espresso active:scale-95"
                >
                  {t.common.contactUs}
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
